const normalizeColor = (value) => String(value || '').trim().toLowerCase();

const getSafePalette = (theme) => {
    const palette = theme?.palette || {};
    const primaryMain = palette.primary?.main || '#1976d2';
    const secondaryMain = palette.secondary?.main || '#00acc1';
    const primaryLight = palette.primary?.light || primaryMain;
    const secondaryLight = palette.secondary?.light || secondaryMain;
    const infoMain = palette.info?.main || primaryLight;
    const successMain = palette.success?.main || secondaryLight;
    const warningMain = palette.warning?.main || secondaryMain;

    return {
        primaryMain,
        secondaryMain,
        primaryLight,
        secondaryLight,
        infoMain,
        successMain,
        warningMain
    };
};

export const getApexBrandColors = (theme, count = 1) => {
    const {
        primaryMain,
        secondaryMain,
        infoMain,
        primaryLight,
        secondaryLight,
        successMain,
        warningMain
    } = getSafePalette(theme);

    const sequence = [
        primaryMain,
        secondaryMain,
        infoMain,
        primaryLight,
        secondaryLight,
        successMain,
        warningMain
    ];

    return Array.from({ length: Math.max(1, count) }, (_, index) => (
        sequence[index % sequence.length]
    ));
};

export const getApexBrandGradientTargets = (theme, count = 1, colors = []) => {
    const { primaryMain, secondaryMain, primaryLight, secondaryLight } = getSafePalette(theme);
    const safeColors = Array.isArray(colors) && colors.length
        ? colors
        : getApexBrandColors(theme, count);

    const primaryToken = normalizeColor(primaryMain);
    const secondaryToken = normalizeColor(secondaryMain);
    const primaryLightToken = normalizeColor(primaryLight);
    const secondaryLightToken = normalizeColor(secondaryLight);

    return safeColors.map((color, index) => {
        const token = normalizeColor(color);
        if (token === primaryToken || token === primaryLightToken) return secondaryMain;
        if (token === secondaryToken || token === secondaryLightToken) return primaryMain;
        return index % 2 === 0 ? secondaryMain : primaryMain;
    });
};

export const applyApexBrandGradient = (options, theme, chartType, config = {}) => {
    const base = options && typeof options === 'object' ? options : {};
    const resolvedType = String(chartType || base?.chart?.type || '').toLowerCase();
    const supportedTypes = new Set(['bar', 'area', 'pie', 'donut']);

    if (!supportedTypes.has(resolvedType)) {
        return base;
    }

    const existingColors = Array.isArray(base.colors)
        ? base.colors.filter(Boolean)
        : [];
    const colorCount = config.colorCount || existingColors.length || 1;
    const colors = config.colors || (existingColors.length
        ? existingColors
        : getApexBrandColors(theme, colorCount));
    const existingGradient = base.fill?.gradient || {};
    const opacityFrom = config.opacityFrom ?? (
        resolvedType === 'area' ? 0.42 : 0.96
    );
    const opacityTo = config.opacityTo ?? (
        resolvedType === 'area' ? 0.06 : 0.82
    );

    return {
        ...base,
        colors,
        fill: {
            ...(base.fill || {}),
            type: 'gradient',
            gradient: {
                shade: 'light',
                type: config.gradientType || 'horizontal',
                shadeIntensity: resolvedType === 'area' ? 0.08 : 0.12,
                inverseColors: false,
                opacityFrom,
                opacityTo,
                stops: [0, 100],
                ...existingGradient,
                gradientToColors: config.gradientToColors
                    || existingGradient.gradientToColors
                    || getApexBrandGradientTargets(theme, colors.length, colors)
            }
        }
    };
};
